import * as Api from '@ViewModels';
import type { AutomationStepNodeData } from '../types/stepNodes';
import { EmailStepRenderer } from './renderers/EmailStepRenderer';
import { TextingStepRenderer } from './renderers/TextingStepRenderer';
import { ActionItemStepRenderer } from './renderers/ActionItemStepRenderer';
import { SwitchStepRenderer } from './renderers/SwitchStepRenderer';
import { TagStepRenderer } from './renderers/TagStepRenderer';
import { OpportunityStepRenderer } from './renderers/OpportunityStepRenderer';
import { HtmlNewsletterStepRenderer } from './renderers/HtmlNewsletterStepRenderer';
import { HandwrittenCardStepRenderer } from './renderers/HandwrittenCardStepRenderer';
import { NoActionStepRenderer } from './renderers/NoActionStepRenderer';

interface AutomationStepCardAdapterProps {
	nodeId: string;
	data: AutomationStepNodeData;
	mode: 'full' | 'compact';
	readOnly?: boolean;
}

export function AutomationStepCardAdapter({ nodeId, data, mode, readOnly = false }: AutomationStepCardAdapterProps) {
	if (mode === 'compact') {
		return <div>Compact view (should not render - handled by wrapper)</div>;
	}

	switch (data.stepType) {
		case Api.AutomationStepType.Email:
			return <EmailStepRenderer nodeId={nodeId} data={data} readOnly={readOnly} />;

		case Api.AutomationStepType.Texting:
			return <TextingStepRenderer nodeId={nodeId} data={data} readOnly={readOnly} />;

		case Api.AutomationStepType.ActionItem:
			return <ActionItemStepRenderer nodeId={nodeId} data={data} readOnly={readOnly} />;

		case Api.AutomationStepType.AddTag:
		case Api.AutomationStepType.RemoveTag:
			return <TagStepRenderer nodeId={nodeId} data={data} readOnly={readOnly} />;

		case Api.AutomationStepType.Opportunity:
			return <OpportunityStepRenderer nodeId={nodeId} data={data} readOnly={readOnly} />;

		case Api.AutomationStepType.HtmlNewsletter:
			return <HtmlNewsletterStepRenderer nodeId={nodeId} data={data} readOnly={readOnly} />;

		case Api.AutomationStepType.HandwrittenCard:
			return <HandwrittenCardStepRenderer nodeId={nodeId} data={data} readOnly={readOnly} />;

		case Api.AutomationStepType.NoAction:
			return <NoActionStepRenderer nodeId={nodeId} data={data} readOnly={readOnly} />;

		case Api.AutomationStepType.Switch:
			return <SwitchStepRenderer nodeId={nodeId} data={data} readOnly={readOnly} />;

		default: {
			const step = data.automationStep;
			const stepName = step && 'name' in step ? step.name : data.stepData?.name;
			const hasContent = data.stepData?.hasContent;
			const schedule = data.stepData?.schedule;

			return (
				<div style={{ padding: 20 }}>
					<div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #E5E7EB' }}>
						<div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 4 }}>
							{stepName || 'Unnamed Step'}
						</div>
						<div style={{ fontSize: 12, color: '#6B7280' }}>{data.stepType}</div>
					</div>

					<div style={{ fontSize: 13, color: '#374151', lineHeight: '1.5' }}>
						{schedule && (
							<div style={{ marginBottom: 8 }}>
								<strong>Schedule:</strong> Scheduled
								{schedule.numberOfDays && ` (${schedule.numberOfDays} days)`}
								{schedule.numberOfHours && ` (${schedule.numberOfHours} hours)`}
							</div>
						)}

						{hasContent !== undefined && (
							<div style={{ marginBottom: 8 }}>
								<strong>Has Content:</strong> {hasContent ? 'Yes' : 'No'}
							</div>
						)}

						{data.isValid !== undefined && (
							<div style={{ marginBottom: 8 }}>
								<strong>Valid:</strong>{' '}
								<span style={{ color: data.isValid ? '#10B981' : '#EF4444' }}>{data.isValid ? 'Yes' : 'No'}</span>
							</div>
						)}
					</div>

					<div
						style={{
							marginTop: 16,
							padding: 12,
							backgroundColor: '#F3F4F6',
							borderRadius: 6,
							fontSize: 11,
							color: '#6B7280',
							textAlign: 'center',
						}}
					>
						Full editing interface coming soon for {data.stepType}
					</div>
				</div>
			);
		}
	}
}
